import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import LandmarkUnlockModal from "./LandmarkUnlockModal";
import InfoMessage from "../common/InfoMessage";
import BookSwitcher from "./BookSwitcher";
import { DEFAULT_BG_IMAGE, getLandmarkResource, HOME_SKINS } from "../../constants/landmarkResources";
import { fetchWithAuth, getApiBaseUrl, API_ENDPOINTS, userApi, learningApi, wordApi } from "../../utils/api";

const BG_STORAGE_KEY = "homepage_selected_landmark_bg_id";

// 玻璃拟态基础样式
const glassBase = {
  background: "rgba(255, 255, 255, 0.22)",
  backdropFilter: "blur(16px)",
  WebkitBackdropFilter: "blur(16px)",
  border: "1px solid rgba(255, 255, 255, 0.35)",
  transition: "all 0.3s ease",
  cursor: "pointer",
  position: "relative",
  zIndex: 9999,
};

// 头像选项列表
const AVATAR_OPTIONS = [
  "😊", "😆", "😒", "😠", "😔", "😉", "😋", "😎", "😍", "😜",
  "🤔", "😮", "😡", "😢", "😭", "😴", "😤", "😷", "🤒", "🤕",
  "🤑", "🤗", "🤓", "😇", "🤠", "🤡", "🤥", "🤫", "🤭", "🤧",
  "🥳", "😱", "😨", "😰", "😥", "😓", "🤩", "🤪", "🤬", "😈",
  "👿", "👹", "👺", "💀", "👻", "👽", "👾", "🤖", "😺", "😸"
];

export default function HomePage({
  state,
  message,
  onShowMessage = () => {},
  onCheckIn = () => {},
  onStartLearn = () => {},
  onStartReview = () => {},
  onGoMyContent = () => {},
  onGoStatistics = () => {},
  onGoMap = () => {},
  onCloseLandmarkUnlock = () => {},
  onGoWordDetail = () => {},
  onLogout = () => {},
  isImporting,
  importProgress,
  hasImportedWords,
  onImportWords = () => {},
  // 皮肤系统 Props
  currentSkinBg,
  shards,
  unlockedSkins,
  currentSkin,
  onChangeSkin,
  onUnlockSkin,
  skinList,
  onUpdateStudyPlan = () => {},
  // ✅ 新增：词书切换功能 Props
  books = [],
  currentBook,
  onSwitchBook = () => {}
}) {
  const navigate = useNavigate();

  // 基础状态
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [pendingVideoLandmark, setPendingVideoLandmark] = useState(null);
  const [preloadedVideo, setPreloadedVideo] = useState(null);

  // 个人主页状态
  const [showProfile, setShowProfile] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [userAvatar, setUserAvatar] = useState("👤");
  const [userEmail, setUserEmail] = useState("");
  const [usernameFromBackend, setUsernameFromBackend] = useState("");
  
  // 学习统计 + 签到状态
  const [studyStats, setStudyStats] = useState(null);
  const [checkInStatus, setCheckInStatus] = useState(null);

  // 学习计划状态
  const [dailyPlan, setDailyPlan] = useState(10);
  const [isEditingPlan, setIsEditingPlan] = useState(false);
  const [planInputValue, setPlanInputValue] = useState("");

  // 皮肤弹窗状态
  const [showSkinModal, setShowSkinModal] = useState(false);

  // 日期格式化
  const today = new Date();
  const dateStr = `${today.getMonth() + 1}/${today.getDate()} ${['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'][today.getDay()]}`;

  // 登录校验 + 获取用户信息
  useEffect(() => {
    const initUserData = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

        try {
          const userData = await userApi.getUserInfo();
          if (userData) {
            setUsernameFromBackend(userData.username);
            setUserAvatar(userData.avatar || "👤");
          }

          try {
            const checkData = await learningApi.getCheckInStatus();
            if (checkData) setCheckInStatus(checkData);
          } catch (err) {
            console.warn("获取签到状态失败", err);
          }

          try {
            const statsData = await learningApi.getLearningStats();
            if (statsData) setStudyStats(statsData);
          } catch (err) {
            console.warn("获取学习统计失败", err);
          }

          try {
            const planData = await userApi.getStudyPlan();
            if (planData) {
              setDailyPlan(planData.daily_new_word_plan);
              setPlanInputValue(planData.daily_new_word_plan.toString());
            }
          } catch (err) {
            console.warn("获取学习计划失败", err);
          }

        } catch (err) {
          console.error("初始化数据失败", err);
        }
    };

    initUserData();
  }, [navigate]);

  // 单词搜索
  useEffect(() => {
    if (!searchInput.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    const lowerInput = searchInput.toLowerCase().trim();
    const matchedWords = APP_WORDS_SOURCE.filter(word => {
      const matchFrench = word.word.toLowerCase().includes(lowerInput) || (word.phonetic && word.phonetic.toLowerCase().includes(lowerInput));
      const matchChinese = word.translation.toLowerCase().includes(lowerInput) || word.common_meanings?.some(m => m.toLowerCase().includes(lowerInput));
      return matchFrench || matchChinese;
    });
    setSearchResults(matchedWords);
    setShowSearchResults(matchedWords.length > 0);
  }, [searchInput]);

  // 签到核心逻辑
  const handleSignIn = async () => {
    if (checkInStatus?.today_checked) {
      onShowMessage("✅ 今日已签到，请勿重复操作！");
      return;
    }

    try {
      await learningApi.checkIn();
      const checkData = await learningApi.getCheckInStatus();
      if (checkData) setCheckInStatus(checkData);
      onShowMessage("✅ 签到成功！");
    } catch (err) {
      onShowMessage("❌ 签到失败");
      console.error(err);
    }
  };

  // 学习计划处理逻辑
  const handleOpenPlanEditor = () => {
    setIsEditingPlan(true);
    setPlanInputValue(dailyPlan.toString());
  };

  const handleSavePlan = async () => {
    const newPlan = parseInt(planInputValue);
    if (isNaN(newPlan) || newPlan < 1 || newPlan > 100) {
      onShowMessage("❌ 请输入 1-100 之间的数字");
      return;
    }

    try {
        await userApi.updateStudyPlan(newPlan);
        setDailyPlan(newPlan);
        try { onUpdateStudyPlan(newPlan); } catch (e) { /* ignore */ }
      setIsEditingPlan(false);
      onShowMessage("✅ 学习计划已更新！");
    } catch (err) {
      onShowMessage("❌ 保存失败");
      console.error(err);
    }
  };

  const handleCancelPlan = () => {
    setIsEditingPlan(false);
    setPlanInputValue(dailyPlan.toString());
  };

  const handlePlanKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSavePlan();
    } else if (e.key === 'Escape') {
      handleCancelPlan();
    }
  };

  // 学习按钮
  const handleGoLearn = () => {
    onShowMessage("即将开始学习新单词");
    setTimeout(() => onStartLearn(), 300);
  };

  // 复习按钮
  const handleGoReview = () => {
    onShowMessage("即将开始复习已学单词");
    setTimeout(() => onStartReview(), 300);
  };

  // 搜索单词点击
  const handleWordItemClick = (word) => {
    setSearchInput("");
    setSearchResults([]);
    setShowSearchResults(false);
    onGoWordDetail(word.id);
    onShowMessage(`查看单词：${word.word}`);
  };

  // 更新头像
  const handleUpdateAvatar = async (emoji) => {
    try {
      const res = await userApi.updateAvatar(emoji);
      if (res) {
        setUserAvatar(emoji);
        setShowAvatarPicker(false);
        onShowMessage("✅ 头像更新成功！");
      }
    } catch (err) {
      onShowMessage("❌ 头像更新失败");
      console.error(err);
    }
  };

  // 退出登录
  const handleLogout = () => {
    const confirmed = window.confirm("确定要退出登录吗？");
    if (confirmed) {
      localStorage.clear();
      onLogout();
      setShowProfile(false);
      navigate("/login");
      onShowMessage("已退出登录");
    }
  };

  // 注销账户
  const handleDeleteAccount = async () => {
    if (window.confirm("⚠️ 确定注销账户？所有数据将永久删除！")) {
      try {
        await userApi.deleteAccount();
        localStorage.clear();
        navigate("/login");
        onShowMessage("账户已注销");
      } catch (err) {
        onShowMessage("注销失败");
        console.error(err);
      }
    }
  };

  // 默认兜底背景 = 埃菲尔铁塔
  const defaultBg = "/eiffel.jpg";

  return (
    <div style={{
      width: "100vw",
      height: "100vh",
      overflow: "hidden",
      background: `url(${currentSkinBg || defaultBg}) center/cover no-repeat fixed`,
      position: "relative",
      margin: 0,
      padding: 0,
    }}>
      {/* 添加CSS动画 */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>

      {/* 背景遮罩 */}
      <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0, 0, 0, 0.15)",
        zIndex: 1,
        pointerEvents: "none",
      }} />

      {/* 顶部区域 */}
      <div style={{
        position: "absolute",
        top: "4vh",
        left: 0,
        width: "100%",
        padding: "0 5vw",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "20px",
        zIndex: 99999,
        pointerEvents: "auto",
      }}>
        <div style={{
          fontSize: "28px",
          fontWeight: 700,
          color: "#fff",
          letterSpacing: "1px",
        }}>
          french Click
        </div>

        {/* 搜索框 */}
        <div style={{
          position: "relative",
          flex: 1,
          maxWidth: "650px",
          zIndex: 99999,
        }}>
          <div style={{
            ...glassBase,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "14px 24px",
            borderRadius: "50px",
            width: "100%",
            boxSizing: "border-box",
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px) scale(1.01)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(0,0,0,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "";
              e.currentTarget.style.boxShadow = "";
            }}
          >
            <span style={{ fontSize: "18px", color: "#fff" }}>🔍</span>
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="输入中文/法语以检索单词"
              style={{
                flex: 1,
                background: "transparent",
                border: "none",
                outline: "none",
                fontSize: "16px",
                color: "#fff",
                width: "100%",
              }}
            />
            <style>{`
              input::placeholder {
                color: #000 !important;
                opacity: 1 !important;
              }
              input::-webkit-input-placeholder {
                color: #000 !important;
                opacity: 1 !important;
              }
              input::-moz-placeholder {
                color: #000 !important;
                opacity: 1 !important;
              }
              input:-ms-input-placeholder {
                color: #000 !important;
                opacity: 1 !important;
              }
            `}</style>

          </div>

          {showSearchResults && (
            <div style={{
              position: "absolute",
              top: "70px",
              left: 0,
              width: "100%",
              maxHeight: "320px",
              overflowY: "auto",
              ...glassBase,
              borderRadius: "16px",
              padding: "12px",
              boxSizing: "border-box",
              zIndex: 99999,
            }}>
              {searchResults.map((word) => (
                <div
                  key={word.id}
                  onClick={() => handleWordItemClick(word)}
                  style={{
                    padding: "10px 12px",
                    borderBottom: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: "8px",
                    cursor: "pointer",
                    transition: "background 0.2s ease",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", color: "#fff", fontSize: "16px", fontWeight: 600 }}>
                    <span>{word.word}</span>
                    <span style={{ fontSize: "14px", opacity: 0.8 }}>{word.phonetic}</span>
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.8)", marginTop: "4px", fontSize: "14px" }}>
                    {word.type} · {word.translation}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 右侧按钮组：碎片 + 皮肤 + 个人中心 */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px", zIndex: 99999 }}>
          {/* 碎片显示 */}
          <div style={{
            ...glassBase,
            padding: "8px 16px",
            borderRadius: "20px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            cursor: "default",
          }}>
            <span>🧩</span>
            <span style={{ color: "#fff", fontSize: "16px", fontWeight: 600 }}>{shards || 0}</span>
          </div>

          {/* 皮肤按钮 */}
          <div
            onClick={() => setShowSkinModal(true)}
            style={{
              ...glassBase,
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "22px",
              pointerEvents: "auto",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            🎨
          </div>

          {/* 个人中心按钮 */}
          <div
            onClick={() => setShowProfile(true)}
            style={{
              ...glassBase,
              width: "50px",
              height: "50px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "22px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            {userAvatar}
          </div>
        </div>
      </div>

      {/* 天气模块 */}
      <div style={{
        position: "absolute",
        top: "110px",
        left: "5vw",
        color: "#fff",
        zIndex: 10,
        fontSize: "14px",
        lineHeight: 1.6,
        textShadow: "0 1px 4px rgba(0,0,0,0.3)",
        pointerEvents: "none",
      }}>
        {state.app.weather.loading ? (
          <>
            <div>加载天气中...</div>
            <div>{state.app.currentDateText}</div>
            <div>{state.app.currentWeekdayText}</div>
          </>
        ) : (
          <>
            <div>{state.app.weather.icon} {state.app.weather.temperature}℃ {state.app.weather.text}</div>
            <div>{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
            <div>{state.app.currentDateText} {state.app.currentWeekdayText}</div>
          </>
        )}
      </div>

      {/* 签到卡片区域 */}
      <div style={{
        position: "absolute",
        top: "220px",
        left: "50px",
        zIndex: 10,
        pointerEvents: "none",
      }}>
        {/* 签到卡片 */}
        <div
          style={{
            ...glassBase,
            width: "160px",
            height: "170px",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            color: "#fff",
            background: checkInStatus?.today_checked ? "rgba(180, 180, 180, 0.22)" : glassBase.background,
            cursor: checkInStatus?.today_checked ? "not-allowed" : "pointer",
            pointerEvents: "auto",
          }}
          onClick={handleSignIn}
          onMouseEnter={(e) => {
            if (checkInStatus?.today_checked) return;
            e.currentTarget.style.transform = "translateY(-6px) scale(1.04)";
            e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.18)";
          }}
          onMouseLeave={(e) => {
            if (checkInStatus?.today_checked) return;
            e.currentTarget.style.transform = "";
            e.currentTarget.style.boxShadow = "";
          }}
        >
          <div style={{ fontSize: "36px", marginBottom: "4px" }}>
            {checkInStatus?.today_checked ? "✅" : ""}
          </div>
          <div style={{ fontSize: "18px", fontWeight: 500 }}>
            {checkInStatus?.today_checked ? "已签到" : "签到"}
          </div>
          <div style={{ fontSize: "14px", opacity: 0.9 }}>{dateStr}</div>
          {checkInStatus && (
            <div style={{ fontSize: "12px", color: "#ffb74d" }}>
              {checkInStatus.continuous_days}天
            </div>
          )}
        </div>
      </div>

      {/* 学习计划卡片区域 */}
      <div style={{
        position: "absolute",
        top: "220px",
        right: "50px",
        zIndex: 10,
        pointerEvents: "none",
      }}>
        {/* 学习计划卡片 */}
        <div
          style={{
            ...glassBase,
            width: "160px",
            height: "170px",
            borderRadius: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            color: "#fff",
            cursor: "pointer",
            pointerEvents: "auto",
          }}
          onClick={!isEditingPlan ? handleOpenPlanEditor : undefined}
          onMouseEnter={(e) => {
            if (isEditingPlan) return;
            e.currentTarget.style.transform = "translateY(-6px) scale(1.04)";
            e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.18)";
          }}
          onMouseLeave={(e) => {
            if (isEditingPlan) return;
            e.currentTarget.style.transform = "";
            e.currentTarget.style.boxShadow = "";
          }}
        >
          {!isEditingPlan ? (
            <>
              <div style={{ fontSize: "36px", marginBottom: "4px" }}>📚</div>
              <div style={{ fontSize: "18px", fontWeight: 500 }}>学习计划</div>
              <div 
                style={{ 
                  fontSize: "40px", 
                  color: "#ffb74d", 
                  fontWeight: 700,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenPlanEditor();
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                {dailyPlan}
              </div>
            </>
          ) : (
            <div style={{ width: "100%", padding: "0 12px", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ fontSize: "13px", fontWeight: 500, marginBottom: "8px", textAlign: "center", opacity: 0.9 }}>
                每日学习计划
              </div>
              <input
                type="number"
                value={planInputValue}
                onChange={(e) => setPlanInputValue(e.target.value)}
                onKeyDown={handlePlanKeyDown}
                style={{
                  width: "80px",
                  padding: "8px",
                  fontSize: "28px",
                  fontWeight: 700,
                  textAlign: "center",
                  background: "rgba(255, 255, 255, 0.25)",
                  border: "2px solid rgba(255, 255, 255, 0.6)",
                  borderRadius: "12px",
                  color: "#ffb74d",
                  outline: "none",
                  marginBottom: "10px",
                  transition: "all 0.2s",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255, 183, 77, 0.8)";
                  e.currentTarget.style.boxShadow = "0 4px 16px rgba(255, 183, 77, 0.3)";
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.6)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.1)";
                }}
                autoFocus
              />
              <div style={{ display: "flex", gap: "8px", justifyContent: "center", width: "100%" }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSavePlan();
                  }}
                  style={{
                    flex: 1,
                    padding: "8px",
                    fontSize: "13px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontWeight: 600,
                    transition: "all 0.2s",
                    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(102, 126, 234, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(102, 126, 234, 0.3)";
                  }}
                >
                  保存
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelPlan();
                  }}
                  style={{
                    flex: 1,
                    padding: "8px",
                    fontSize: "13px",
                    background: "rgba(255, 255, 255, 0.25)",
                    color: "#fff",
                    border: "1px solid rgba(255, 255, 255, 0.5)",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontWeight: 600,
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.35)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255, 255, 255, 0.25)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  取消
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 🔥 词书切换轮播组件（红色标记区域） */}
      <BookSwitcher
        books={books}
        currentBook={currentBook}
        onSwitchBook={onSwitchBook}
      />

      {/* 学习+复习按钮 */}
      <div style={{
        position: "absolute",
        bottom: "18vh",
        left: 0,
        width: "100%",
        padding: "0 20vw",
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "40px",
        zIndex: 10,
        pointerEvents: "none",
      }}>
        <div
          style={{
            ...glassBase,
            width: "220px",
            height: "85px",
            borderRadius: "50px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            flexShrink: 0,
            pointerEvents: "auto",
          }}
          onClick={handleGoLearn}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-6px) scale(1.05)";
            e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "";
            e.currentTarget.style.boxShadow = "";
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#fff", fontSize: "20px", fontWeight: 500 }}>
            <span>🎓</span>
            <span>Apprendre</span>
          </div>
          <div style={{ color: "#ffb74d", fontSize: "18px", fontWeight: 600 }}>
            {studyStats?.today_new_word_count || 0}
          </div>
        </div>

        <div
          style={{
            ...glassBase,
            width: "220px",
            height: "85px",
            borderRadius: "50px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            flexShrink: 0,
            pointerEvents: "auto",
          }}
          onClick={handleGoReview}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-6px) scale(1.05)";
            e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "";
            e.currentTarget.style.boxShadow = "";
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#fff", fontSize: "20px", fontWeight: 500 }}>
            <span>🔄</span>
            <span>Réviser</span>
          </div>
          <div style={{ color: "#ffb74d", fontSize: "18px", fontWeight: 600 }}>
            {studyStats?.today_review_count || 0}
          </div>
        </div>
      </div>

      {/* 底部按钮 */}
      <div style={{
        position: "absolute",
        bottom: "5vh",
        left: 0,
        width: "100%",
        padding: "0 12vw",
        boxSizing: "border-box",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        zIndex: 10,
      }}>
        {/* 壁纸按钮 */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
        }}>
          <div
            style={{
              ...glassBase,
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "22px",
              color: "#fff",
              transition: "all 0.3s ease",
            }}
            onClick={(e) => {e.stopPropagation(); onGoMap(); onShowMessage("查看解锁地图及兑换商店");}}
            onMouseEnter={(e) => {e.currentTarget.style.transform = "scale(1.08)";}}
            onMouseLeave={(e) => {e.currentTarget.style.transform = "scale(1)";}}
          >
            🗺️
          </div>
          <span style={{
            color: "#fff",
            fontSize: "12px",
            fontWeight: 500,
            textShadow: "0 2px 4px rgba(0,0,0,0.3)",
          }}>壁纸</span>
        </div>

        {/* 我的内容按钮 */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
        }}>
          <div
            style={{
              ...glassBase,
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "22px",
              color: "#fff",
              transition: "all 0.3s ease",
            }}
            onClick={(e) => {e.stopPropagation(); onGoMyContent(); onShowMessage("查看在背词书、星级单词本");}}
            onMouseEnter={(e) => {e.currentTarget.style.transform = "scale(1.08)";}}
            onMouseLeave={(e) => {e.currentTarget.style.transform = "scale(1)";}}
          >
            📖
          </div>
          <span style={{
            color: "#fff",
            fontSize: "12px",
            fontWeight: 500,
            textShadow: "0 2px 4px rgba(0,0,0,0.3)",
          }}>我的内容</span>
        </div>

        {/* 学习情况按钮 */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px",
        }}>
          <div
            style={{
              ...glassBase,
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "22px",
              color: "#fff",
              transition: "all 0.3s ease",
            }}
            onClick={(e) => {e.stopPropagation(); onGoStatistics(); onShowMessage("查看遗忘曲线及学习情况");}}
            onMouseEnter={(e) => {e.currentTarget.style.transform = "scale(1.08)";}}
            onMouseLeave={(e) => {e.currentTarget.style.transform = "scale(1)";}}
          >
            📊
          </div>
          <span style={{
            color: "#fff",
            fontSize: "12px",
            fontWeight: 500,
            textShadow: "0 2px 4px rgba(0,0,0,0.3)",
          }}>学习情况</span>
        </div>
      </div>

      {/* 单词导入 */}
      {isImporting && (
        <div style={{
          position: "fixed",
          bottom: "20px",
          right: "20px",
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(10px)",
          padding: "1rem 1.5rem",
          borderRadius: "14px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          zIndex: 99999,
          minWidth: "200px",
        }}>
          <div style={{ fontWeight: 700, marginBottom: "0.5rem", color: "#333" }}>
            📥 正在导入单词...
          </div>
          <div style={{
            width: "100%",
            height: "8px",
            background: "#f0f0f0",
            borderRadius: "4px",
            overflow: "hidden",
          }}>
            <div style={{
              width: `${importProgress}%`,
              height: "100%",
              background: "#667eea",
              transition: "width 0.3s ease",
            }} />
          </div>
          <div style={{ marginTop: "0.5rem", fontSize: "0.9rem", color: "#666" }}>
            {importProgress}%
          </div>
        </div>
      )}

      {!hasImportedWords && !isImporting && (
        <button
          onClick={onImportWords}
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            background: "#667eea",
            color: "#fff",
            border: "none",
            padding: "0.8rem 1.2rem",
            borderRadius: "12px",
            fontWeight: 700,
            cursor: "pointer",
            boxShadow: "0 4px 16px rgba(102,126,234,0.4)",
            zIndex: 99998,
            transition: "transform 0.2s ease",
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
          onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
        >
          📥 导入单词到后端
        </button>
      )}

      {/* 皮肤弹窗 */}
      {showSkinModal && (
        <div style={{
          position: "fixed", 
          inset: 0,
          background: "rgba(0,0,0,0.7)",
          zIndex: 999999,
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
        }}
        onClick={(e) => e.target === e.currentTarget && setShowSkinModal(false)}
        >
          <div style={{
            background: "#fff",
            width: "90%",
            maxWidth: "600px",
            padding: "24px",
            borderRadius: "20px",
            maxHeight: "80vh",
            overflowY: "auto",
            pointerEvents: "auto",
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2 style={{ margin: 0, fontSize: "24px" }}>🎨 背景皮肤</h2>
              <button 
                onClick={() => setShowSkinModal(false)} 
                style={{ 
                  fontSize: "24px", 
                  cursor: "pointer", 
                  background: "none", 
                  border: "none",
                  padding: "0 8px"
                }}
              >×</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
              {(skinList || HOME_SKINS).map(skin => {
                // 埃菲尔铁塔 强制默认免费解锁
                const isEiffelSkin = skin.name?.includes("埃菲尔") || skin.id === "eiffel";
                const isLandmarkSkin = skin.id.startsWith("landmark_");
                const landmarkId = isLandmarkSkin ? parseInt(skin.id.replace("landmark_", "")) : null;
                const isUnlocked = isEiffelSkin 
                  ? true 
                  : isLandmarkSkin 
                    ? state.user.unlockedLandmarks?.includes(landmarkId)
                    : (unlockedSkins || []).includes(skin.id);
                const isCurrent = currentSkin === skin.id;

                return (
                  <div key={skin.id} style={{ border: "1px solid #eee", borderRadius: "12px", overflow: "hidden" }}>
                    <img 
                      src={skin.bgUrl} 
                      style={{ width: "100%", height: "120px", objectFit: "cover" }} 
                      alt={skin.name} 
                      onError={(e) => {
                        e.target.src = "https://picsum.photos/400/240";
                      }}
                    />
                    <div style={{ padding: "12px" }}>
                      <div style={{ fontWeight: 700 }}>{skin.name}</div>
                      <div style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}>
                        {isEiffelSkin ? "✨ 免费默认" : skin.isDefault ? "默认解锁" : `需要 ${skin.needShards} 🧩`}
                      </div>

                      {isUnlocked ? (
                        <button 
                          onClick={() => {
                            onChangeSkin(skin.id);
                            setShowSkinModal(false);
                            onShowMessage(`✅ 已切换：${skin.name}`);
                          }}
                          style={{ 
                            width: "100%", padding: "8px", marginTop: "8px",
                            background: isCurrent ? "#667eea" : "#eee",
                            border: "none", borderRadius: "8px", cursor: "pointer",
                            color: isCurrent ? "#fff" : "#333" 
                          }}>
                          {isCurrent ? "已使用" : "使用皮肤"}
                        </button>
                      ) : (
                        <button 
                          onClick={() => {
                            if (isLandmarkSkin) {
                              onShowMessage("❌ 请先前往地图解锁该地标！");
                            } else {
                              onUnlockSkin(skin.id);
                            }
                          }}
                          style={{ 
                            width: "100%", padding: "8px", marginTop: "8px",
                            background: "#ffb74d", border: "none", borderRadius: "8px",
                            cursor: "pointer", color: "#fff" 
                          }}>
                          {isLandmarkSkin ? "需解锁地标" : "解锁皮肤"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* 个人主页弹窗 */}
      {showProfile && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(8px)",
          zIndex: 999999,
          display: "flex", alignItems: "center", justifyContent: "center",
          animation: "fadeIn 0.3s ease",
        }}
        onClick={(e) => e.target === e.currentTarget && setShowProfile(false)}
        >
          <div style={{
            width: "90%", maxWidth: "1000px", maxHeight: "90vh",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)",
            borderRadius: "28px",
            boxShadow: "0 25px 80px rgba(0,0,0,0.25)",
            position: "relative",
            display: "flex",
            overflow: "hidden",
            pointerEvents: "auto",
            animation: "slideUp 0.4s ease",
          }}>
            {/* 左侧区域:头像和基本信息 */}
            <div style={{
              flex: "0 0 350px",
              background: "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "60px 40px 40px 40px",
              position: "relative",
              overflow: "hidden",
            }}>
              {/* 背景装饰 */}
              <div style={{
                position: "absolute",
                top: "-50%",
                right: "-50%",
                width: "300px",
                height: "300px",
                background: "rgba(255,255,255,0.1)",
                borderRadius: "50%",
              }} />
              <div style={{
                position: "absolute",
                bottom: "-30%",
                left: "-30%",
                width: "250px",
                height: "250px",
                background: "rgba(255,255,255,0.08)",
                borderRadius: "50%",
              }} />

              {/* 头像 */}
              <div
                onClick={() => setShowAvatarPicker(true)}
                style={{
                  width: "130px",
                  height: "130px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                  border: "4px solid rgba(255,255,255,0.4)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "56px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  position: "relative",
                  zIndex: 1,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.08)";
                  e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.2)";
                }}
              >
                {userAvatar}
              </div>
              <p style={{ marginTop: "20px", color: "rgba(255,255,255,0.9)", fontSize: "15px", cursor: "pointer", fontWeight: 500 }} onClick={() => setShowAvatarPicker(true)}>
                点击更换头像
              </p>

              {/* 用户名 */}
              <div style={{ marginTop: "30px", textAlign: "center", position: "relative", zIndex: 1 }}>
                <h2 style={{ margin: "0 0 8px 0", fontSize: "28px", color: "#fff", fontWeight: 700 }}>
                  {usernameFromBackend || "加载中..."}
                </h2>
                <p style={{ margin: 0, fontSize: "14px", color: "rgba(255,255,255,0.8)" }}>
                  Nom d'utilisateur
                </p>
              </div>

              {/* 碎片和签到统计 */}
              <div style={{
                marginTop: "30px",
                width: "100%",
                display: "flex",
                gap: "15px",
                position: "relative",
                zIndex: 1,
              }}>
                {/* 碎片卡片 */}
                <div style={{
                  flex: 1,
                  background: "rgba(255,255,255,0.15)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "16px",
                  padding: "16px 12px",
                  textAlign: "center",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}>
                  <div style={{ fontSize: "28px", marginBottom: "6px" }}>🧩</div>
                  <div style={{ fontSize: "22px", fontWeight: 700, color: "#fff" }}>{shards || 0}</div>
                  <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.8)", marginTop: "4px" }}>碎片</div>
                </div>

                {/* 连续签到 */}
                <div style={{
                  flex: 1,
                  background: "rgba(255,255,255,0.15)",
                  backdropFilter: "blur(10px)",
                  borderRadius: "16px",
                  padding: "16px 12px",
                  textAlign: "center",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}>
                  <div style={{ fontSize: "28px", marginBottom: "6px" }}>🔥</div>
                  <div style={{ fontSize: "22px", fontWeight: 700, color: "#fff" }}>
                    {checkInStatus?.continuous_days || 0}
                  </div>
                  <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.8)", marginTop: "4px" }}>连续签到</div>
                </div>
              </div>
            </div>

            {/* 右侧区域:详细信息和设置 */}
            <div style={{
              flex: 1,
              padding: "40px",
              overflowY: "auto",
            }}>
              {/* 关闭按钮 */}
              <button
                onClick={() => setShowProfile(false)}
                style={{
                  position: "absolute", top: 24, right: 24,
                  background: "rgba(0,0,0,0.05)", border: "none",
                  fontSize: "28px", cursor: "pointer", color: "#666",
                  width: "40px", height: "40px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  borderRadius: "50%",
                  transition: "all 0.2s",
                  zIndex: 10,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(0,0,0,0.1)";
                  e.currentTarget.style.transform = "rotate(90deg)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(0,0,0,0.05)";
                  e.currentTarget.style.transform = "rotate(0deg)";
                }}
              >×</button>

              {/* 标题 */}
              <div style={{ marginBottom: "32px" }}>
                <h2 style={{ margin: 0, fontSize: "26px", color: "#1a1a1a" }}>个人主页</h2>
                <p style={{ margin: "8px 0 0 0", color: "#999", fontSize: "14px" }}>Profil Personnel</p>
              </div>

              {/* 学习统计卡片 */}
              {studyStats && (
                <div style={{
                  marginBottom: "28px",
                  background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                  borderRadius: "20px",
                  padding: "24px",
                }}>
                  <h3 style={{ margin: "0 0 16px 0", fontSize: "16px", color: "#333", fontWeight: 600 }}>
                    📊 学习统计
                  </h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
                    <div style={{
                      background: "#fff",
                      borderRadius: "14px",
                      padding: "16px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}>
                      <div style={{ fontSize: "12px", color: "#999", marginBottom: "6px" }}>今日学习</div>
                      <div style={{ fontSize: "24px", fontWeight: 700, color: "#667eea" }}>
                        {studyStats.today_duration_minutes}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>分钟</div>
                    </div>
                    <div style={{
                      background: "#fff",
                      borderRadius: "14px",
                      padding: "16px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}>
                      <div style={{ fontSize: "12px", color: "#999", marginBottom: "6px" }}>今日复习</div>
                      <div style={{ fontSize: "24px", fontWeight: 700, color: "#f093fb" }}>
                        {studyStats.today_review_count}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>次</div>
                    </div>
                    <div style={{
                      background: "#fff",
                      borderRadius: "14px",
                      padding: "16px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}>
                      <div style={{ fontSize: "12px", color: "#999", marginBottom: "6px" }}>今日新词</div>
                      <div style={{ fontSize: "24px", fontWeight: 700, color: "#4facfe" }}>
                        {studyStats.today_new_word_count}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>个</div>
                    </div>
                    <div style={{
                      background: "#fff",
                      borderRadius: "14px",
                      padding: "16px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                    }}>
                      <div style={{ fontSize: "12px", color: "#999", marginBottom: "6px" }}>总掌握</div>
                      <div style={{ fontSize: "24px", fontWeight: 700, color: "#43e97b" }}>
                        {studyStats.total_mastered_words}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>词</div>
                    </div>
                  </div>
                </div>
              )}

              {/* 设置菜单 */}
              <div>
                <p style={{ color: "#999", fontSize: "13px", margin: "0 0 14px 0", fontWeight: 500, textTransform: "uppercase", letterSpacing: "1px" }}>
                  设置 · Paramètres
                </p>
                <div style={{
                  background: "#fff",
                  borderRadius: "18px",
                  overflow: "hidden",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
                  border: "1px solid #f0f0f0",
                }}>
                  {[
                    { icon: "👤", label: "账号信息", action: null },
                    { icon: "💡", label: "帮助与反馈", action: null },
                    { icon: "⭐", label: "评价应用", action: null },
                    { icon: "", label: "服务条款", action: null },
                  ].map((item, index) => (
                    <div
                      key={index}
                      style={{
                        padding: "18px 20px",
                        borderBottom: index < 3 ? "1px solid #f5f5f5" : "none",
                        cursor: item.action ? "pointer" : "default",
                        fontSize: "15px",
                        color: "#333",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        transition: "all 0.2s",
                      }}
                      onClick={() => item.action && item.action()}
                      onMouseEnter={(e) => {
                        if (item.action) {
                          e.currentTarget.style.background = "#f8f9ff";
                          e.currentTarget.style.paddingLeft = "24px";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.paddingLeft = "20px";
                      }}
                    >
                      <span style={{ fontSize: "20px" }}>{item.icon}</span>
                      <span style={{ flex: 1 }}>{item.label}</span>
                      <span style={{ color: "#ccc", fontSize: "18px" }}>›</span>
                    </div>
                  ))}
                  <div
                    onClick={handleLogout}
                    style={{
                      padding: "18px 20px",
                      borderBottom: "1px solid #f5f5f5",
                      cursor: "pointer",
                      fontSize: "15px",
                      color: "#ff6b6b",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#fff5f5";
                      e.currentTarget.style.paddingLeft = "24px";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.paddingLeft = "20px";
                    }}
                  >
                    <span style={{ fontSize: "20px" }}>🚪</span>
                    <span style={{ flex: 1, fontWeight: 600 }}>退出登录</span>
                    <span style={{ color: "#ff6b6b", fontSize: "18px" }}>›</span>
                  </div>
                  <div
                    onClick={handleDeleteAccount}
                    style={{
                      padding: "18px 20px",
                      cursor: "pointer",
                      fontSize: "15px",
                      color: "#ff4757",
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#fff0f0";
                      e.currentTarget.style.paddingLeft = "24px";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.paddingLeft = "20px";
                    }}
                  >
                    <span style={{ fontSize: "20px" }}>⚠️</span>
                    <span style={{ flex: 1, fontWeight: 600 }}>注销账号</span>
                    <span style={{ color: "#ff4757", fontSize: "18px" }}>›</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 头像选择器 */}
      {showAvatarPicker && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.6)",
          zIndex: 999999,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
        onClick={(e) => e.target === e.currentTarget && setShowAvatarPicker(false)}
        >
          <div style={{
            background: "#fff",
            borderRadius: "16px",
            padding: "24px",
            width: "90%",
            maxWidth: "400px",
            pointerEvents: "auto",
          }}>
            <h3 style={{ margin: "0 0 16px 0", textAlign: "center" }}>选择头像</h3>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: "12px",
            }}>
              {AVATAR_OPTIONS.map((emoji, index) => (
                <div
                  key={index}
                  onClick={() => handleUpdateAvatar(emoji)}
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    background: "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px",
                    cursor: "pointer",
                    transition: "transform 0.2s",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.1)"}
                  onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                >
                  {emoji}
                </div>
              ))}
            </div>
            <button
              onClick={() => setShowAvatarPicker(false)}
              style={{
                width: "100%",
                marginTop: "16px",
                padding: "12px",
                background: "#eee",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >取消</button>
          </div>
        </div>
      )}

      <LandmarkUnlockModal
        landmark={state.ui.showLandmarkUnlock}
        onClose={onCloseLandmarkUnlock}
        onEnter={() => {}}
        getLandmarkResource={getLandmarkResource}
      />

      {/* 视频弹窗 */}
      {pendingVideoLandmark && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,.85)", zIndex: 99999,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
        onClick={(e) => e.target === e.currentTarget && setPendingVideoLandmark(null)}
        >
          <div style={{ borderRadius: "12px", overflow: "hidden", boxShadow: "0 10px 40px #000", pointerEvents: "auto" }}>
            <video autoPlay src={preloadedVideo} onEnded={() => setPendingVideoLandmark(null)} style={{ width: "600px", maxWidth: "90vw", display: "block" }} />
            <button onClick={() => setPendingVideoLandmark(null)} style={{
              width: "100%", padding: "12px", background: "#222", color: "#fff", border: "none", cursor: "pointer", textAlign: "center"
            }}>关闭</button>
          </div>
        </div>
      )}

      <InfoMessage message={message} />
    </div>
  );
}